import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import {
  User,
  Mail,
  Building2,
  Scale,
  GraduationCap,
  Save,
  Edit3,
  X,
  AlertCircle,
  Check
} from 'lucide-react';

const ROLE_OPTIONS = [
  'Defense Attorney (Private Practice)',
  'Public Defender',
  'Prosecutor',
  'Paralegal',
  'Legal Researcher',
  'Journalist',
  'Academic/Researcher',
  'Law Student',
  'Private Investigator',
  'Policy Advocate',
  'Civil Rights Organization',
  'Insurance Professional',
  'Other'
];

const BAR_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'District of Columbia'
];

const ProfileSettings = ({ onClose }) => {
  const { currentUser, updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    role: '',
    barNumber: '',
    barState: '',
    organization: ''
  });

  // Initialize form data from current user
  useEffect(() => {
    if (currentUser) {
      setFormData({
        displayName: currentUser.displayName || '',
        email: currentUser.email || '',
        role: currentUser.role || '',
        barNumber: currentUser.barNumber || '',
        barState: currentUser.barState || '',
        organization: currentUser.organization || ''
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!formData.displayName || !formData.role) {
      setError('Please fill in all required fields');
      return;
    }

    const attorneyRoles = [
      'Defense Attorney (Private Practice)',
      'Public Defender',
      'Prosecutor'
    ];
    if (!attorneyRoles.includes(formData.role) && !formData.organization) {
      setError('Please provide your organization or institution');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);

      // Prepare update data
      const updateData = {
        displayName: formData.displayName,
        role: formData.role,
        barNumber: formData.barNumber || null,
        barState: formData.barState || null,
        organization: formData.organization || null,
        updatedAt: new Date().toISOString()
      };

      await updateUserProfile(updateData);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Profile update error:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to current user data
    if (currentUser) {
      setFormData({
        displayName: currentUser.displayName || '',
        email: currentUser.email || '',
        role: currentUser.role || '',
        barNumber: currentUser.barNumber || '',
        barState: currentUser.barState || '',
        organization: currentUser.organization || ''
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <User className="h-6 w-6 text-gray-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            Profile Settings
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Profile Form */}
      <div className="space-y-6">
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              disabled={!isEditing}
              className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                isEditing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
              }`}
              placeholder="Enter your full name"
            />
          </div>
        </div>

        {/* Email (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              value={formData.email}
              disabled
              className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-500 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Email address cannot be changed. Contact support if needed.
          </p>
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Your Role
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Scale className="h-5 w-5 text-gray-400" />
            </div>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={!isEditing}
              className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                isEditing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <option value="">Select your role</option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bar Number and State - only show for attorneys */}
        {[
          'Defense Attorney (Private Practice)',
          'Public Defender',
          'Prosecutor'
        ].includes(formData.role) && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bar Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <GraduationCap className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="barNumber"
                  value={formData.barNumber}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    isEditing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                  }`}
                  placeholder="Enter your bar number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State/Bar Association
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  name="barState"
                  value={formData.barState}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    isEditing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <option value="">Select state</option>
                  {BAR_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Organization */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {[
              'Defense Attorney (Private Practice)',
              'Public Defender',
              'Prosecutor'
            ].includes(formData.role)
              ? 'Law Firm/Organization (Optional)'
              : 'Organization/Institution'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              disabled={!isEditing}
              className={`appearance-none block w-full pl-10 pr-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                isEditing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
              }`}
              placeholder={
                [
                  'Defense Attorney (Private Practice)',
                  'Public Defender',
                  'Prosecutor'
                ].includes(formData.role)
                  ? 'Law firm, government agency, etc.'
                  : 'University, media outlet, government agency, etc.'
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ProfileSettings.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default ProfileSettings;
